import { MRT_RowSelectionState, MRT_Row } from "material-react-table";
import { useState, useEffect, useMemo } from "react";
import { JobFlags, ImportTableColumn } from "../../../../utilities/types";
import { toast } from "react-toastify";
import { API } from "../../../../api";
import type { Job, Contact } from "../../../../models";
import { defaultJobForImport } from "../../../../models";
import { CREATE_NEW_SALESPERSON } from "../../../../utilities/constants";
import { Oops } from "../../../../utilities/oops";
import { generateUUID, yieldToMain } from "../../../../utilities/utils";
import { FileDownloader } from "../../../../utilities/downloadFile";
import {
	validateJobs as validateJobsShared,
	FIELD_TO_PROPERTY_MAP,
	ValidationContext,
	parseValidationErrorKey,
	getValidationErrorKey,
	ValidationMessages,
} from "../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../admin/ImportSettings";
import {
	ImportTableViewModel,
	JobsImportTableProps,
	ValidationOptions,
	MergeResolutions,
	MergeConflict,
} from "./ImportTable.types";
import {
	allJobsHaveNoFiles,
	findDeal,
	updateJobGroup,
	dealBasedSubRowUpdate,
	detectMergeConflicts as detectMergeConflictsHelper,
	getMergedValue,
	getMergeableProperties,
} from "./ImportTable.helpers";
import { GeoData } from "@rogoag/shared-ts";
import { FeatureCollection } from "geojson";
import { ColumnContext } from "./columns/standard-column-types";

export function useImportTableViewModel(props: JobsImportTableProps): ImportTableViewModel {
	// ============= STATE DECLARATIONS =============
	const [jobs, setJobs] = useState<Job[]>(props.jobs);
	const [readySelectionState, setReadySelectionState] = useState<Record<string, boolean>>({});
	const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
	const [isInitializing, setIsInitializing] = useState(true);
	// Track locally created salespersons that haven't been submitted yet
	const [newSalespersons, setNewSalespersons] = useState<Contact[]>([]);

	// ============= DROPDOWN OPTIONS STATE =============
	const [gridSizes] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.density;
			}),
			...props.jobs.flatMap((job) => (job.importDensity ? [job.importDensity] : [])),
		]),
	]);
	const [depths] = useState<number[]>([
		...new Set([
			...props.allDeals.flatMap((deal) => {
				return deal.depth;
			}),
			...props.jobs.flatMap((job) => (job.importDepth ? [job.importDepth] : [])),
		]),
	]);
	const [labPackages] = useState<string[]>([
		...new Set<string>([
			...props.allDeals.flatMap((deal) => {
				const field = deal.testPackage;
				return field !== undefined && field !== "" ? field : [];
			}),
			...props.jobs.flatMap((job) => (job.externalTestPackageId ? [job.externalTestPackageId] : [])),
			...["No test package specified"],
		]),
	]);
	const [micros] = useState<number[]>([
		...new Set(
			props.allDeals.flatMap((deal) => {
				return deal.frequencyOfMicros ?? 0;
			}),
		),
		...props.jobs.flatMap((job) => (job.externalFrequencyOfMicros ? [job.externalFrequencyOfMicros] : [])),
	]);

	// ============= VALIDATION CONTEXT =============
	const validationContext: ValidationContext = {
		company: props.selectedCompany,
		allDeals: props.allDeals,
		integration: props.integration,
	};

	// ============= HELPER FUNCTIONS =============
	// Helper function to get row-specific validation context (includes the deal for that row)
	const getRowValidationContext = (row: MRT_Row<Job>): ValidationContext => {
		const job = jobs.find((j) => j.id === row.original.id);
		const deal = props.allDeals?.find((d) => d.id === job?.dealIds?.[0]);
		const isCreatingNewSalesperson = job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON) ?? false;
		const hasPointsFile = true; // makes microsList visible; it will be disabled if no points file

		return {
			...validationContext,
			job,
			deal,
			isCreatingNewSalesperson,
			hasPointsFile,
		};
	};

	// Helper function to detect if a salesperson is locally created (has temp ID)
	const isNewSalesperson = (id: string): boolean => {
		return id.startsWith("temp-");
	};

	// Helper function to find salesperson data by ID from local list
	const findNewSalesperson = (id: string): Contact | undefined => {
		return newSalespersons.find((sp) => sp.id === id);
	};

	// ============= BUSINESS LOGIC FUNCTIONS =============
	const validateJobs = (
		jobsToValidate: Job[],
		options: ValidationOptions = { updateState: true, clearPrevious: true },
	): Record<string, string> => {
		const { updateState = true, clearPrevious = true } = options;

		// Use shared validation function
		const errors = validateJobsShared(jobsToValidate, validationContext);

		// Update state if requested
		if (updateState) {
			if (clearPrevious) {
				setValidationErrors(errors);
			} else {
				// Merge new errors and remove any that are no longer present for these jobs
				setValidationErrors((prev) => {
					const newErrors = { ...prev, ...errors };

					// For each job being validated, remove any previous errors that are no longer valid
					for (const job of jobsToValidate) {
						Object.entries(FIELD_TO_PROPERTY_MAP).forEach(([fieldType]) => {
							const errorKey = getValidationErrorKey(fieldType as ImportTableColumn, job.id);
							// If no new error was generated for this field, remove any existing error
							if (!errors[errorKey]) {
								delete newErrors[errorKey];
							}
						});
					}

					return newErrors;
				});
			}
		}

		return errors;
	};

	// Map-related functions - pass selected jobs to the map component
	function updateDisplayedJobs(rowSelection: MRT_RowSelectionState) {
		if (!props.setDisplayedJobs) {
			return;
		}

		// rowSelection keys are job IDs (due to getRowId config), not indices
		const selectedJobIds = new Set(Object.keys(rowSelection).filter((key) => rowSelection[key]));

		// Filter jobs by selected IDs - O(n) instead of O(n*m)
		const selectedJobs = jobs.filter((job) => selectedJobIds.has(job.id));

		props.setDisplayedJobs(selectedJobs);
	}

	// ============= EFFECTS =============
	// Initialize jobs with deal matching and validation - chunked to prevent UI freeze
	useEffect(() => {
		const abortController = new AbortController();

		async function initializeJobs() {
			setIsInitializing(true);
			const signal = abortController.signal;

			try {
				// Phase 1: Match deals in chunks to prevent UI freeze
				const CHUNK_SIZE = 50;
				const updatedJobs: Array<Job & { deal: string }> = [];

				for (let i = 0; i < props.jobs.length; i += CHUNK_SIZE) {
					if (signal.aborted) return;

					const chunk = props.jobs.slice(i, Math.min(i + CHUNK_SIZE, props.jobs.length));

					for (const job of chunk) {
						const matchedDeal = findDeal(
							props.allDeals,
							job.importDensity ?? 0,
							job.importDepth ?? 0,
							job.externalTestPackageId ?? "",
							job.externalFrequencyOfMicros ?? 0,
						);
						const dealId = matchedDeal?.id || "custom";
						updatedJobs.push({
							...job,
							deal: dealId.toString(),
							dealIds: [dealId],
						});
					}

					// Yield to main thread between chunks (not after last chunk)
					if (i + CHUNK_SIZE < props.jobs.length) {
						await yieldToMain();
					}
				}

				if (signal.aborted) return;

				// Phase 2: Update state with all matched jobs
				setJobs(updatedJobs);
				await yieldToMain();

				// After setJobs, always call setIsInitializing(false) even if aborted
				// because jobs are now in state and should be displayed
				if (signal.aborted) {
					setIsInitializing(false);
					return;
				}

				// Phase 3: Validate in chunks (same CHUNK_SIZE for consistency)
				const allErrors: Record<string, string> = {};

				for (let i = 0; i < updatedJobs.length; i += CHUNK_SIZE) {
					if (signal.aborted) {
						setIsInitializing(false);
						return;
					}

					const chunk = updatedJobs.slice(i, Math.min(i + CHUNK_SIZE, updatedJobs.length));
					const chunkErrors = validateJobsShared(chunk, validationContext);
					Object.assign(allErrors, chunkErrors);

					// Yield to main thread between chunks
					if (i + CHUNK_SIZE < updatedJobs.length) {
						await yieldToMain();
					}
				}

				// After setJobs, always call setIsInitializing(false) even if aborted
				if (signal.aborted) {
					setIsInitializing(false);
					return;
				}

				// Phase 4: Final state updates
				setValidationErrors(allErrors);
				setIsInitializing(false);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") {
					// Jobs may have been set before abort, so ensure initialization ends
					setIsInitializing(false);
					return;
				}
				console.error("Error during job initialization:", err);
				setIsInitializing(false);
			}
		}

		initializeJobs();

		return () => {
			abortController.abort();
		};
	}, [props.jobs, props.allDeals, props.selectedCompany]);

	// Notify parent component about validation state changes
	useEffect(() => {
		const hasValidationErrors = Object.keys(validationErrors).length > 0;
		if (props.onValidationChange) {
			props.onValidationChange(hasValidationErrors);
		}
	}, [validationErrors, props.onValidationChange]);

	// ============= COLUMN CONTEXT =============
	// Create context object with all dependencies that column builders need
	// Memoized to prevent unnecessary column rebuilds
	// NOTE: validationErrors is NOT included here - it's accessed via ValidationErrorsContext
	// to prevent column rebuilds when validation errors change
	const columnContext: ColumnContext = useMemo(
		() => ({
			props,
			jobs,
			setJobs,
			validateJobs,
			validationContext,
			newSalespersons,
			setNewSalespersons,
			isNewSalesperson,
			findNewSalesperson,
			readySelectionState,
			setReadySelectionState,
			gridSizes,
			depths,
			labPackages,
			micros,
			getRowValidationContext,
			findDeal,
			updateJobGroup,
			dealBasedSubRowUpdate,
		}),
		[props, jobs, validationContext, newSalespersons, readySelectionState, gridSizes, depths, labPackages, micros],
	);

	// ============= EXPORT/SUBMIT FUNCTIONS =============
	function hasValidationErrors() {
		return Object.keys(validationErrors).length > 0;
	}

	function getValidationErrorSummary(): {
		missing: { field: string; count: number }[];
		invalid: { field: string; count: number }[];
	} {
		const missingCounts: Record<string, number> = {};
		const invalidCounts: Record<string, number> = {};

		Object.entries(validationErrors).forEach(([key, error]) => {
			if (error) {
				// Parse the error key using centralized helper
				const fieldType = parseValidationErrorKey(key);

				let displayName: string;
				if (fieldType && columnDisplayNames[fieldType]) {
					// Use the centralized display names dictionary
					displayName = columnDisplayNames[fieldType];
				} else if (fieldType) {
					// Fallback: format the field type nicely
					displayName = fieldType.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
				} else {
					// Malformed key (should not happen with standardized pattern)
					console.warn(`Unexpected validation error key format: ${key}`);
					displayName = "Unknown Field";
				}

				// Categorize as missing vs invalid by comparing error message
				const isMissing =
					fieldType && ValidationMessages[fieldType]?.required && error === ValidationMessages[fieldType].required;

				if (isMissing) {
					missingCounts[displayName] = (missingCounts[displayName] || 0) + 1;
				} else {
					invalidCounts[displayName] = (invalidCounts[displayName] || 0) + 1;
				}
			}
		});

		return {
			missing: Object.entries(missingCounts)
				.map(([field, count]) => ({ field, count }))
				.sort((a, b) => b.count - a.count),
			invalid: Object.entries(invalidCounts)
				.map(([field, count]) => ({ field, count }))
				.sort((a, b) => b.count - a.count),
		};
	}

	function exportCSV() {
		try {
			// Define the CSV headers
			const headers = [
				columnDisplayNames.grower,
				columnDisplayNames.farm,
				columnDisplayNames.field,
				columnDisplayNames.isReadyToSample,
				columnDisplayNames.density,
				columnDisplayNames.depth,
				columnDisplayNames.testPackage,
				columnDisplayNames.frequencyOfMicros,
				columnDisplayNames.fieldPriority,
				columnDisplayNames.salesperson,
				columnDisplayNames.lastCrop,
				columnDisplayNames.nextCrop,
				columnDisplayNames.yieldGoal,
				columnDisplayNames.dateFieldReadyEstimated,
				columnDisplayNames.labSubmissionCode,
				columnDisplayNames.labInstructions,
				columnDisplayNames.growerPhoneNumber,
				columnDisplayNames.submissionNotes,
				columnDisplayNames.fieldId,
				columnDisplayNames.submitterName,
				columnDisplayNames.submitterEmail,
			];

			// Build CSV rows
			const csvRows = [headers.join(",")];

			for (const job of jobs) {
				const row = [
					job.importGrowerName || "",
					job.importFarmName || "",
					job.importFieldName || "",
					job.isReadyToSample ? "Yes" : "No",
					job.importDensity?.toString() || "",
					job.importDepth?.toString() || "",
					job.externalTestPackageId || "",
					job.externalFrequencyOfMicros?.toString() || "",
					job.fieldPriority || "Regular Turn (default)",
					// Get salesperson names from IDs
					(job.salespersonIds || [])
						.map((id) => props.salespersons?.find((sp) => sp.id === id)?.name || "")
						.filter((name) => name)
						.join("; "),
					job.lastCrop || "",
					job.nextCrop || "",
					job.yieldGoal || "",
					job.dateFieldReadyEstimated?.toLocaleDateString() || "",
					job.labSubmissionCode || "",
					job.labInstructions || "",
					job.growerPhoneNumber || "",
					job.submissionNotes || "",
					job.fieldId || "",
					job.submitterName || "",
					job.submitterEmail || "",
				];

				// Escape fields that contain commas, quotes, or newlines
				const escapedRow = row.map((field) => {
					if (field.includes(",") || field.includes('"') || field.includes("\n")) {
						return `"${field.replace(/"/g, '""')}"`;
					}
					return field;
				});

				csvRows.push(escapedRow.join(","));
			}

			// Create and download the CSV file
			const csv = csvRows.join("\n");
			const blob = new Blob([csv], { type: "text/csv" });
			const filename = `job_import_${new Date().toISOString().split("T")[0]}.csv`;
			FileDownloader.downloadBlob(blob, filename);

			toast.success(`Exported ${jobs.length} jobs to CSV`);
		} catch (error) {
			Oops.handleError(error, "ImportTable.exportCSV");
			toast.error("Failed to export CSV");
		}
	}

	function addBlankJob() {
		const newJob = defaultJobForImport();
		const updatedJobs = [...jobs, newJob];
		setJobs(updatedJobs);
		// Only validate the new job, not all jobs (incremental validation)
		validateJobs([newJob], { updateState: true, clearPrevious: false });
	}

	function deleteJobs(indices: number[]) {
		const indicesToDelete = new Set(indices);
		const deletedJobs = jobs.filter((_, index) => indicesToDelete.has(index));
		const updatedJobs = jobs.filter((_, index) => !indicesToDelete.has(index));
		setJobs(updatedJobs);

		// Clear validation errors for deleted jobs (no need to re-validate remaining jobs)
		setValidationErrors((prev) => {
			const newErrors = { ...prev };
			for (const job of deletedJobs) {
				// Remove all errors associated with this job
				Object.keys(newErrors).forEach((key) => {
					if (key.endsWith(`-${job.id}`)) {
						delete newErrors[key];
					}
				});
			}
			return newErrors;
		});
	}

	function duplicateJobs(indices: number[]) {
		const jobsToDuplicate = indices.map((i) => jobs[i]);
		const duplicatedJobs = jobsToDuplicate.map((job) => ({
			...job,
			id: generateUUID(),
		}));
		const updatedJobs = [...jobs, ...duplicatedJobs];
		setJobs(updatedJobs);
		// Only validate the new duplicated jobs (incremental validation)
		validateJobs(duplicatedJobs, { updateState: true, clearPrevious: false });
	}

	function detectMergeConflicts(indices: number[]): MergeConflict[] {
		if (indices.length < 2) return [];
		const sortedIndices = [...indices].sort((a, b) => a - b);
		const jobsToMerge = sortedIndices.map((i) => jobs[i]);
		return detectMergeConflictsHelper(jobsToMerge);
	}

	function mergeJobs(indices: number[], resolutions?: MergeResolutions) {
		if (indices.length < 2) return;

		// Sort indices to ensure consistent ordering
		const sortedIndices = [...indices].sort((a, b) => a - b);
		const jobsToMerge = sortedIndices.map((i) => jobs[i]);
		const baseJob = jobsToMerge[0];

		// Helper to merge GeoJSON collections, filtering out undefined values
		const mergeGeoJSON = (collections: (typeof baseJob.files.boundaryGeoJSON)[]) => {
			const valid = collections.filter((c): c is NonNullable<typeof c> => !!c);
			return valid.length > 0 ? GeoData.merge(valid) : undefined;
		};

		// Build merged job using truthy-wins logic for mergeable properties
		const mergedJob: Job = {
			...baseJob,
			files: {
				...baseJob.files,
				boundaryGeoJSON: mergeGeoJSON(jobsToMerge.map((j) => j.files?.boundaryGeoJSON)),
				pointsGeoJSON: mergeGeoJSON(jobsToMerge.map((j) => j.files?.pointsGeoJSON)),
				zonesGeoJSON: mergeGeoJSON(jobsToMerge.map((j) => j.files?.zonesGeoJSON)),
			},
		};

		// Apply truthy-wins logic for all mergeable properties
		for (const propKey of getMergeableProperties()) {
			const mergedValue = getMergedValue(jobsToMerge, propKey, resolutions);
			// TypeScript doesn't know that propKey is a valid key, so we need to cast
			(mergedJob as any)[propKey] = mergedValue;
		}

		// Remove merged jobs and insert the combined job at the first position
		const indicesToRemove = new Set(sortedIndices);
		const removedJobs = jobs.filter((_, index) => indicesToRemove.has(index));
		const updatedJobs = jobs.filter((_, index) => !indicesToRemove.has(index));
		updatedJobs.splice(sortedIndices[0], 0, mergedJob);

		setJobs(updatedJobs);

		// Clear errors for removed jobs, then validate only the merged job (incremental)
		setValidationErrors((prev) => {
			const newErrors = { ...prev };
			for (const job of removedJobs) {
				Object.keys(newErrors).forEach((key) => {
					if (key.endsWith(`-${job.id}`)) {
						delete newErrors[key];
					}
				});
			}
			return newErrors;
		});
		validateJobs([mergedJob], { updateState: true, clearPrevious: false });
	}

	// Feature ID parsing patterns for splitJob (UUIDs contain dashes, so use regex)
	const FEATURE_ID_PATTERNS = {
		boundary: /^boundary-(.+)-(\d+)-(\d+)$/,
		zones: /^zones-(.+)-(\d+)-(\d+)$/,
		points: /^points-(.+)-(\d+)$/,
	};

	function splitJob(jobIndex: number, selectedFeatureIds: Set<string>, onClearSelection?: () => void): void {
		const job = jobs[jobIndex];
		if (!job?.id) {
			toast.error("Cannot split: job has no ID");
			return;
		}

		if (selectedFeatureIds.size === 0) {
			toast.error("Cannot split: no features selected");
			return;
		}

		try {
			// Parse selected feature IDs to get indices by type
			const boundaryIndices = new Set<number>();
			const pointsIndices = new Set<number>();
			const zonesIndices = new Set<number>();

			for (const featureId of selectedFeatureIds) {
				// Try each pattern
				let match = featureId.match(FEATURE_ID_PATTERNS.boundary);
				if (match && match[1] === job.id) {
					boundaryIndices.add(parseInt(match[2]));
					continue;
				}
				match = featureId.match(FEATURE_ID_PATTERNS.zones);
				if (match && match[1] === job.id) {
					zonesIndices.add(parseInt(match[2]));
					continue;
				}
				match = featureId.match(FEATURE_ID_PATTERNS.points);
				if (match && match[1] === job.id) {
					pointsIndices.add(parseInt(match[2]));
					continue;
				}
			}

			// Helper to filter FeatureCollection
			const filterFeatures = (
				collection: FeatureCollection | undefined,
				indices: Set<number>,
				mode: "include" | "exclude",
			): FeatureCollection | undefined => {
				if (!collection?.features) return undefined;
				const filtered = collection.features.filter((_, i) => (mode === "include" ? indices.has(i) : !indices.has(i)));
				if (filtered.length === 0) return undefined;
				return { ...collection, features: filtered };
			};

			// Create new job with selected features
			const newBoundary = filterFeatures(job.files?.boundaryGeoJSON, boundaryIndices, "include");
			const newPoints = filterFeatures(job.files?.pointsGeoJSON, pointsIndices, "include");
			const newZones = filterFeatures(job.files?.zonesGeoJSON, zonesIndices, "include");

			// Create completely new files object to avoid reference sharing
			const newJobFiles = {
				// Copy non-GeoJSON file references
				pointsSHP: job.files?.pointsSHP,
				boundarySHP: job.files?.boundarySHP,
				zonesSHP: job.files?.zonesSHP,
				executedPointsSHP: job.files?.executedPointsSHP,
				executedBoundarySHP: job.files?.executedBoundarySHP,
				executedPointsBoundarySHP: job.files?.executedPointsBoundarySHP,
				labPDF: job.files?.labPDF,
				labCSV: job.files?.labCSV,
				labResults: job.files?.labResults,
				fieldNotes: job.files?.fieldNotes,
				// Set GeoJSON properties explicitly
				boundaryGeoJSON: newBoundary,
				pointsGeoJSON: newPoints,
				zonesGeoJSON: newZones,
			};

			// Create fileOptions for the new job - clear options for types that have no data
			// This prevents auto-selection from re-adding GeoJSON that was intentionally excluded
			const newJobFileOptions = {
				...job.fileOptions,
				// Clear boundary options if new job has no boundary
				boundaryOptions: newBoundary ? job.fileOptions.boundaryOptions : [],
				// Clear points options if new job has no points
				pointsOptions: newPoints ? job.fileOptions.pointsOptions : [],
				// Clear zones options if new job has no zones
				zonesOptions: newZones ? job.fileOptions.zonesOptions : [],
			};

			const newJobId = generateUUID();
			// Create a shallow copy and explicitly set the properties we need
			const newJob: Job = {
				...job,
				id: newJobId,
				files: newJobFiles,
				fileOptions: newJobFileOptions,
			};

			// Update original job to exclude selected features
			const originalBoundary = filterFeatures(job.files?.boundaryGeoJSON, boundaryIndices, "exclude");
			const originalPoints = filterFeatures(job.files?.pointsGeoJSON, pointsIndices, "exclude");
			const originalZones = filterFeatures(job.files?.zonesGeoJSON, zonesIndices, "exclude");

			const originalJobFiles = {
				// Copy non-GeoJSON file references
				pointsSHP: job.files?.pointsSHP,
				boundarySHP: job.files?.boundarySHP,
				zonesSHP: job.files?.zonesSHP,
				executedPointsSHP: job.files?.executedPointsSHP,
				executedBoundarySHP: job.files?.executedBoundarySHP,
				executedPointsBoundarySHP: job.files?.executedPointsBoundarySHP,
				labPDF: job.files?.labPDF,
				labCSV: job.files?.labCSV,
				labResults: job.files?.labResults,
				fieldNotes: job.files?.fieldNotes,
				// Set GeoJSON properties with filtered features
				boundaryGeoJSON: originalBoundary,
				pointsGeoJSON: originalPoints,
				zonesGeoJSON: originalZones,
			};

			// Create fileOptions for the original job - clear options for types that have no data
			const originalJobFileOptions = {
				...job.fileOptions,
				boundaryOptions: originalBoundary ? job.fileOptions.boundaryOptions : [],
				pointsOptions: originalPoints ? job.fileOptions.pointsOptions : [],
				zonesOptions: originalZones ? job.fileOptions.zonesOptions : [],
			};

			const originalJob: Job = {
				...job,
				files: originalJobFiles,
				fileOptions: originalJobFileOptions,
			};

			// Replace original and insert new after it
			const updatedJobs = [...jobs];
			updatedJobs[jobIndex] = originalJob;
			updatedJobs.splice(jobIndex + 1, 0, newJob);

			setJobs(updatedJobs);

			// Clear errors for original job ID, then validate only the 2 affected jobs (incremental)
			setValidationErrors((prev) => {
				const newErrors = { ...prev };
				Object.keys(newErrors).forEach((key) => {
					if (key.endsWith(`-${job.id}`)) {
						delete newErrors[key];
					}
				});
				return newErrors;
			});
			validateJobs([originalJob, newJob], { updateState: true, clearPrevious: false });
			onClearSelection?.();

			// Clear displayed jobs immediately to avoid stale closure issue
			// The useEffect will re-populate when selection changes
			props.setDisplayedJobs?.([]);
		} catch (error) {
			Oops.handleError(error, "ImportTable.splitJob");
			toast.error("Failed to split job");
		}
	}

	async function createJobs() {
		props.setLoading("submit");

		// Then validate all required fields
		if (hasValidationErrors()) {
			toast.error("Fill out required fields");
			props.setLoading(false);
			return;
		}

		if (allJobsHaveNoFiles(jobs)) {
			if (!window.confirm("No geodata files attached. Proceed?")) {
				return;
			}
		}

		const creating = toast.loading(`Creating ${jobs.length} Jobs...`);

		jobs.forEach((job) => {
			if (!job.jobFlags.includes("Bulk Upload")) job.jobFlags.push("Bulk Upload");
			job.dealIds = job.dealIds[0] === "custom" ? [] : job.dealIds;
		});

		try {
			let source: JobFlags;
			switch (props.integration) {
				case "Proagrica":
					source = "Created Portal API Proagrica";
					break;
				case "Agrian":
					source = "Created Portal API Agrian";
					break;
				default:
					source = "Created Portal Bulk";
					break;
			}
			await API.Data.Jobs.create.many(jobs, source);
			toast.success(`Created ${jobs.length} Jobs! Please give 1-2 business days for jobs to show up in the dashboard.`);
			props.resetForm();
		} catch (error: any) {
			Oops.handleError(error, "SubmitJobs.JobImportTable.createJobs");
		} finally {
			toast.dismiss(creating);
			props.setLoading(false);
		}
	}

	// ============= RETURN VIEW MODEL =============
	return {
		columnContext,
		isInitializing,
		jobs,
		validationErrors,
		setValidationErrors,
		updateDisplayedJobs,
		hasValidationErrors,
		getValidationErrorSummary,
		exportCSV,
		createJobs,
		addBlankJob,
		deleteJobs,
		duplicateJobs,
		mergeJobs,
		detectMergeConflicts,
		splitJob,
	};
}
